import { useState, useEffect, useRef } from 'react';

/**
 * Billing interval options for subscription plans.
 */
const BillingInterval = {
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  LIFETIME: 'lifetime',
} as const;
type BillingIntervalType = (typeof BillingInterval)[keyof typeof BillingInterval];

/**
 * Subscription plan status options.
 */
const SubscriptionPlanStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  ARCHIVED: 'archived',
} as const;
type SubscriptionPlanStatusType = (typeof SubscriptionPlanStatus)[keyof typeof SubscriptionPlanStatus];

/**
 * Plan features structure for feature lists.
 */
export interface PlanFeatures {
  featureList: string[];
  highlights: string[];
  excludedFeatures: string[];
}

/**
 * Plan limits structure for usage limits.
 */
export interface PlanLimits {
  messagesPerDay?: number;
  maxConversationHistory?: number;
  voiceEnabled?: boolean;
  mediaUploadsEnabled?: boolean;
}

/**
 * Platform IDs structure for active payment provider integrations.
 */
export interface PlatformIds {
  stripePriceId?: string;
  stripeProductId?: string;
  appleProductId?: string;
  googleProductId?: string;
}

export type PlanPlatformIds = PlatformIds & Record<string, unknown>;
type EditablePlatformIds = PlanPlatformIds;

const EDITABLE_PLATFORM_ID_KEYS: Array<keyof PlatformIds> = [
  'stripePriceId',
  'stripeProductId',
  'appleProductId',
  'googleProductId',
];

function sanitizePlatformIds(platformIds?: Record<string, unknown> | null): PlatformIds | undefined {
  if (!platformIds) {
    return undefined;
  }

  const nextPlatformIds: PlatformIds = {};

  if (typeof platformIds.stripePriceId === 'string' && platformIds.stripePriceId.trim()) {
    nextPlatformIds.stripePriceId = platformIds.stripePriceId.trim();
  }
  if (typeof platformIds.stripeProductId === 'string' && platformIds.stripeProductId.trim()) {
    nextPlatformIds.stripeProductId = platformIds.stripeProductId.trim();
  }
  if (typeof platformIds.appleProductId === 'string' && platformIds.appleProductId.trim()) {
    nextPlatformIds.appleProductId = platformIds.appleProductId.trim();
  }
  if (typeof platformIds.googleProductId === 'string' && platformIds.googleProductId.trim()) {
    nextPlatformIds.googleProductId = platformIds.googleProductId.trim();
  }

  return Object.keys(nextPlatformIds).length > 0 ? nextPlatformIds : undefined;
}

function sanitizeLegacyPlatformIds(platformIds?: Record<string, unknown> | null): Record<string, unknown> | undefined {
  if (!platformIds) {
    return undefined;
  }

  const nextPlatformIds = Object.fromEntries(
    Object.entries(platformIds).flatMap(([key, value]) => {
      if (EDITABLE_PLATFORM_ID_KEYS.includes(key as keyof PlatformIds) || value == null) {
        return [];
      }

      if (typeof value === 'string') {
        const trimmedValue = value.trim();
        return trimmedValue ? [[key, trimmedValue] as const] : [];
      }

      return [[key, value] as const];
    }),
  );

  return Object.keys(nextPlatformIds).length > 0 ? nextPlatformIds : undefined;
}

function getEditablePlatformIds(platformIds?: Record<string, unknown> | null): EditablePlatformIds {
  if (!platformIds) {
    return {};
  }

  const trimmedPlatformIds: EditablePlatformIds = { ...platformIds };
  const sanitizedPlatformIds = sanitizePlatformIds(platformIds);

  for (const key of EDITABLE_PLATFORM_ID_KEYS) {
    if (sanitizedPlatformIds?.[key]) {
      trimmedPlatformIds[key] = sanitizedPlatformIds[key];
    } else {
      delete trimmedPlatformIds[key];
    }
  }

  return trimmedPlatformIds;
}

function getLegacyPlatformIdEntries(platformIds: EditablePlatformIds): Array<[string, unknown]> {
  return Object.entries(platformIds).filter(
    ([key]) => !EDITABLE_PLATFORM_ID_KEYS.includes(key as keyof PlatformIds),
  );
}

function buildPlatformIdsForSubmit(editedPlatformIds: EditablePlatformIds): PlanPlatformIds | undefined {
  const sanitizedPlatformIds = sanitizePlatformIds(editedPlatformIds);
  const preservedLegacyPlatformIds = sanitizeLegacyPlatformIds(editedPlatformIds);
  const nextPlatformIds = {
    ...preservedLegacyPlatformIds,
    ...sanitizedPlatformIds,
  };

  return Object.keys(nextPlatformIds).length > 0 ? nextPlatformIds : undefined;
}

function updatePlatformId(
  currentPlatformIds: EditablePlatformIds,
  key: keyof PlatformIds,
  value: string
): EditablePlatformIds {
  return { ...currentPlatformIds, [key]: value };
}

function removePlatformId(currentPlatformIds: EditablePlatformIds, key: string): EditablePlatformIds {
  const nextPlatformIds = { ...currentPlatformIds };
  delete nextPlatformIds[key];
  return nextPlatformIds;
}

/**
 * Plan data structure for editing.
 */
export interface PlanData {
  id: string;
  name: string;
  planKey: string;
  description: string | null;
  priceAmount: number;
  currency: string;
  billingInterval: BillingIntervalType;
  intervalCount?: number;
  status: SubscriptionPlanStatusType | null;
  features?: PlanFeatures | null;
  limits?: PlanLimits | null;
  platformIds?: PlanPlatformIds | null;
  trialDays?: number;
  isDefault?: boolean;
  isFeatured?: boolean;
}

/**
 * Form data structure for create/update operations.
 */
export interface PlanFormData {
  name: string;
  planKey: string;
  description?: string;
  priceAmount: number;
  currency: string;
  billingInterval: BillingIntervalType;
  intervalCount: number;
  status: SubscriptionPlanStatusType;
  features?: PlanFeatures;
  limits?: PlanLimits;
  platformIds?: PlanPlatformIds;
  trialDays?: number;
  isDefault?: boolean;
  isFeatured?: boolean;
}

interface PlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: PlanFormData & { id?: string }) => Promise<void>;
  editingPlan?: PlanData | null;
  isSubmitting?: boolean;
  error?: string;
}

/**
 * Modal component for creating or editing a subscription plan.
 *
 * Provides a form for administrators to define a plan with:
 * - Name (required)
 * - Plan Key (required)
 * - Description (optional)
 * - Price Amount (required)
 * - Currency (required)
 * - Billing Interval (required)
 * - Status (required)
 */
export function PlanModal({
  isOpen,
  onClose,
  onSubmit,
  editingPlan,
  isSubmitting = false,
  error,
}: PlanModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [name, setName] = useState('');
  const [planKey, setPlanKey] = useState('');
  const [description, setDescription] = useState('');
  const [priceAmount, setPriceAmount] = useState<string>('0');
  const [currency, setCurrency] = useState('USD');
  const [billingInterval, setBillingInterval] = useState<BillingIntervalType>(BillingInterval.MONTHLY);
  const [intervalCount, setIntervalCount] = useState<string>('1');
  const [status, setStatus] = useState<SubscriptionPlanStatusType>(SubscriptionPlanStatus.ACTIVE);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Features state
  const [featureList, setFeatureList] = useState<string[]>([]);
  const [highlights, setHighlights] = useState<string[]>([]);
  const [excludedFeatures, setExcludedFeatures] = useState<string[]>([]);

  // Limits state (empty string = unlimited)
  const [messagesPerDay, setMessagesPerDay] = useState<string>('');
  const [maxConversationHistory, setMaxConversationHistory] = useState<string>('');
  const [voiceEnabled, setVoiceEnabled] = useState<boolean>(false);
  const [mediaUploadsEnabled, setMediaUploadsEnabled] = useState<boolean>(false);

  // Platform IDs state
  const [platformIds, setPlatformIds] = useState<EditablePlatformIds>({});

  // Display settings state
  const [trialDays, setTrialDays] = useState<string>('0');
  const [isDefault, setIsDefault] = useState<boolean>(false);
  const [isFeatured, setIsFeatured] = useState<boolean>(false);

  const isEditMode = !!editingPlan;

  // Reset/initialize form when modal opens or editingPlan changes
  useEffect(() => {
    if (isOpen) {
      if (editingPlan) {
        // Edit mode: pre-fill with existing plan data
        setName(editingPlan.name);
        setPlanKey(editingPlan.planKey);
        setDescription(editingPlan.description || '');
        setPriceAmount(String(editingPlan.priceAmount));
        setCurrency(editingPlan.currency);
        setBillingInterval(editingPlan.billingInterval);
        setIntervalCount(String(editingPlan.intervalCount ?? 1));
        setStatus(editingPlan.status || SubscriptionPlanStatus.ACTIVE);
        // Initialize features
        setFeatureList(editingPlan.features?.featureList ?? []);
        setHighlights(editingPlan.features?.highlights ?? []);
        setExcludedFeatures(editingPlan.features?.excludedFeatures ?? []);
        // Initialize limits
        setMessagesPerDay(editingPlan.limits?.messagesPerDay?.toString() ?? '');
        setMaxConversationHistory(editingPlan.limits?.maxConversationHistory?.toString() ?? '');
        setVoiceEnabled(editingPlan.limits?.voiceEnabled ?? false);
        setMediaUploadsEnabled(editingPlan.limits?.mediaUploadsEnabled ?? false);
        // Initialize platform IDs
        setPlatformIds(getEditablePlatformIds(editingPlan.platformIds as Record<string, unknown> | null | undefined));
        // Initialize display settings
        setTrialDays(editingPlan.trialDays?.toString() ?? '0');
        setIsDefault(editingPlan.isDefault ?? false);
        setIsFeatured(editingPlan.isFeatured ?? false);
      } else {
        // Create mode: reset to defaults
        setName('');
        setPlanKey('');
        setDescription('');
        setPriceAmount('0');
        setCurrency('USD');
        setBillingInterval(BillingInterval.MONTHLY);
        setIntervalCount('1');
        setStatus(SubscriptionPlanStatus.ACTIVE);
        // Reset features
        setFeatureList([]);
        setHighlights([]);
        setExcludedFeatures([]);
        // Reset limits
        setMessagesPerDay('');
        setMaxConversationHistory('');
        setVoiceEnabled(false);
        setMediaUploadsEnabled(false);
        // Reset platform IDs
        setPlatformIds({});
        // Reset display settings
        setTrialDays('0');
        setIsDefault(false);
        setIsFeatured(false);
      }
      setValidationError(null);
    }
  }, [isOpen, editingPlan]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside modal to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Helper functions for dynamic array manipulation
  const handleAddFeature = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    maxItems: number,
    currentItems: string[]
  ) => {
    if (currentItems.length < maxItems) {
      setter([...currentItems, '']);
    }
  };

  const handleUpdateFeature = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    index: number,
    value: string,
    currentItems: string[]
  ) => {
    const updated = [...currentItems];
    updated[index] = value;
    setter(updated);
  };

  const handleRemoveFeature = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    index: number,
    currentItems: string[]
  ) => {
    setter(currentItems.filter((_, i) => i !== index));
  };

  // Dynamic string array input component
  const renderFeatureArrayInput = (
    label: string,
    items: string[],
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    maxItems: number,
    placeholder: string,
    helpText: string
  ) => (
    <div style={styles.formGroup}>
      <label style={styles.label}>{label}</label>
      <div style={styles.featureListContainer}>
        {items.map((item, index) => (
          <div key={index} style={styles.featureInputRow}>
            <input
              type="text"
              value={item}
              onChange={(e) => handleUpdateFeature(setter, index, e.target.value, items)}
              placeholder={placeholder}
              style={styles.featureInput}
              maxLength={500}
              disabled={isSubmitting}
            />
            <button
              type="button"
              onClick={() => handleRemoveFeature(setter, index, items)}
              style={styles.removeFeatureButton}
              disabled={isSubmitting}
              aria-label="Remove feature"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        ))}
        {items.length < maxItems && (
          <button
            type="button"
            onClick={() => handleAddFeature(setter, maxItems, items)}
            style={styles.addFeatureButton}
            disabled={isSubmitting}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Add item
          </button>
        )}
      </div>
      <span style={styles.helpText}>
        {helpText} ({items.length}/{maxItems})
      </span>
    </div>
  );

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Validate required fields
    if (!name.trim()) {
      setValidationError('Plan name is required');
      return;
    }

    if (!planKey.trim()) {
      setValidationError('Plan key is required');
      return;
    }

    // Validate plan key format: must start with letter, only lowercase letters, numbers, underscores
    const planKeyRegex = /^[a-z][a-z0-9_]*$/;
    if (!planKeyRegex.test(planKey.trim())) {
      setValidationError('Plan key must start with a letter and contain only lowercase letters, numbers, and underscores');
      return;
    }

    const parsedPrice = parseInt(priceAmount, 10);
    if (isNaN(parsedPrice) || parsedPrice < 0) {
      setValidationError('Price must be a non-negative number');
      return;
    }

    const parsedIntervalCount = parseInt(intervalCount, 10);
    if (isNaN(parsedIntervalCount) || parsedIntervalCount < 1 || parsedIntervalCount > 365) {
      setValidationError('Interval count must be a whole number between 1 and 365');
      return;
    }

    // Validate currency format: 3 uppercase letters
    const currencyRegex = /^[A-Z]{3}$/;
    if (!currencyRegex.test(currency.trim())) {
      setValidationError('Currency must be a valid ISO 4217 code (e.g., USD, EUR, GBP)');
      return;
    }

    // Filter out empty feature strings
    const cleanedFeatureList = featureList.map(f => f.trim()).filter(f => f.length > 0);
    const cleanedHighlights = highlights.map(f => f.trim()).filter(f => f.length > 0);
    const cleanedExcludedFeatures = excludedFeatures.map(f => f.trim()).filter(f => f.length > 0);

    // Only include features if at least one array has items
    const hasFeatures = cleanedFeatureList.length > 0 || cleanedHighlights.length > 0 || cleanedExcludedFeatures.length > 0;

    // Build limits object, converting empty strings to undefined for unlimited
    const parsedMessagesPerDay = messagesPerDay.trim() ? parseInt(messagesPerDay, 10) : undefined;
    const parsedMaxConversationHistory = maxConversationHistory.trim() ? parseInt(maxConversationHistory, 10) : undefined;

    // Only include limits if at least one value is set
    const hasLimits = parsedMessagesPerDay !== undefined ||
                      parsedMaxConversationHistory !== undefined ||
                      voiceEnabled ||
                      mediaUploadsEnabled;

    const limitsData: PlanLimits | undefined = hasLimits ? {
      ...(parsedMessagesPerDay !== undefined && { messagesPerDay: parsedMessagesPerDay }),
      ...(parsedMaxConversationHistory !== undefined && { maxConversationHistory: parsedMaxConversationHistory }),
      ...(voiceEnabled && { voiceEnabled }),
      ...(mediaUploadsEnabled && { mediaUploadsEnabled }),
    } : undefined;

    const platformIdsData = buildPlatformIdsForSubmit(platformIds);

    // Parse trial days
    const parsedTrialDays = parseInt(trialDays, 10);
    const validTrialDays = !isNaN(parsedTrialDays) && parsedTrialDays >= 0 ? parsedTrialDays : 0;

    const formData: PlanFormData & { id?: string } = {
      name: name.trim(),
      planKey: planKey.trim(),
      description: description.trim() || undefined,
      priceAmount: parsedPrice,
      currency: currency.trim(),
      billingInterval,
      intervalCount: parsedIntervalCount,
      status,
      features: hasFeatures ? {
        featureList: cleanedFeatureList,
        highlights: cleanedHighlights,
        excludedFeatures: cleanedExcludedFeatures,
      } : undefined,
      limits: limitsData,
      platformIds: platformIdsData,
      trialDays: validTrialDays,
      isDefault,
      isFeatured,
    };

    if (editingPlan) {
      formData.id = editingPlan.id;
    }

    try {
      await onSubmit(formData);
    } catch {
      // Error handling is done by parent component
    }
  };

  const isValid = name.trim() && planKey.trim() && currency.trim();
  const legacyPlatformIds = getLegacyPlatformIdEntries(platformIds);

  return (
    <div style={styles.overlay}>
      <div ref={modalRef} style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={styles.headerIcon}>{isEditMode ? '\u270E' : '\u2728'}</span>
            <div>
              <h2 style={styles.title}>{isEditMode ? 'Edit Plan' : 'Create Plan'}</h2>
              <p style={styles.subtitle}>
                {isEditMode ? 'Modify subscription plan details' : 'Define a new subscription plan'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={styles.closeButton}
            aria-label="Close modal"
          >
            &#215;
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.content}>
            {/* Error Display */}
            {(validationError || error) && (
              <div style={styles.errorMessage}>
                {validationError || error}
              </div>
            )}

            {/* Name Field */}
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="plan-name">
                Plan Name <span style={styles.required}>*</span>
              </label>
              <input
                id="plan-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Free Plan, Pro Monthly"
                style={styles.input}
                maxLength={100}
                disabled={isSubmitting}
                autoFocus
              />
              <span style={styles.helpText}>
                Display name of the plan (max 100 characters)
              </span>
            </div>

            {/* Plan Key Field */}
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="plan-key">
                Plan Key <span style={styles.required}>*</span>
              </label>
              <input
                id="plan-key"
                type="text"
                value={planKey}
                onChange={(e) => setPlanKey(e.target.value.toLowerCase())}
                placeholder="e.g., free, basic, pro, premium"
                style={styles.input}
                maxLength={50}
                disabled={isSubmitting}
              />
              <span style={styles.helpText}>
                Internal identifier (lowercase letters, numbers, underscores only)
              </span>
            </div>

            {/* Description Field */}
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="plan-description">
                Description
              </label>
              <textarea
                id="plan-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the plan and its features..."
                style={styles.textarea}
                rows={3}
                maxLength={1000}
                disabled={isSubmitting}
              />
              <span style={styles.helpText}>
                Optional description of the plan (max 1000 characters)
              </span>
            </div>

            {/* Price and Currency Row */}
            <div style={styles.formRow}>
              {/* Price Amount Field */}
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label} htmlFor="plan-price">
                  Price Amount <span style={styles.required}>*</span>
                </label>
                <input
                  id="plan-price"
                  type="number"
                  value={priceAmount}
                  onChange={(e) => setPriceAmount(e.target.value)}
                  placeholder="0"
                  style={styles.input}
                  min={0}
                  disabled={isSubmitting}
                />
                <span style={styles.helpText}>
                  Price in smallest currency unit (e.g., cents)
                </span>
              </div>

              {/* Currency Field */}
              <div style={{ ...styles.formGroup, flex: 1 }}>
                <label style={styles.label} htmlFor="plan-currency">
                  Currency <span style={styles.required}>*</span>
                </label>
                <input
                  id="plan-currency"
                  type="text"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                  placeholder="USD"
                  style={styles.input}
                  maxLength={3}
                  disabled={isSubmitting}
                />
                <span style={styles.helpText}>
                  ISO 4217 code
                </span>
              </div>
            </div>

            {/* Billing Interval, Interval Count, and Status Row */}
            <div style={styles.formRow}>
              {/* Billing Interval Field */}
              <div style={{ ...styles.formGroup, flex: 1 }}>
                <label style={styles.label} htmlFor="plan-interval">
                  Billing Interval <span style={styles.required}>*</span>
                </label>
                <select
                  id="plan-interval"
                  value={billingInterval}
                  onChange={(e) => setBillingInterval(e.target.value as BillingIntervalType)}
                  style={styles.select}
                  disabled={isSubmitting}
                >
                  <option value={BillingInterval.WEEKLY}>Weekly</option>
                  <option value={BillingInterval.MONTHLY}>Monthly</option>
                  <option value={BillingInterval.YEARLY}>Yearly</option>
                  <option value={BillingInterval.LIFETIME}>Lifetime</option>
                </select>
                <span style={styles.helpText}>
                  How often users are billed
                </span>
              </div>

              {/* Interval Count Field */}
              <div style={{ ...styles.formGroup, flex: 1 }}>
                <label style={styles.label} htmlFor="plan-interval-count">
                  Every <span style={styles.required}>*</span>
                </label>
                <input
                  id="plan-interval-count"
                  type="number"
                  value={intervalCount}
                  onChange={(e) => setIntervalCount(e.target.value)}
                  placeholder="1"
                  style={styles.input}
                  min={1}
                  max={365}
                  disabled={isSubmitting || billingInterval === BillingInterval.LIFETIME}
                />
                <span style={styles.helpText}>
                  e.g., 3 + Monthly = every 3 months
                </span>
              </div>

              {/* Status Field */}
              <div style={{ ...styles.formGroup, flex: 1 }}>
                <label style={styles.label} htmlFor="plan-status">
                  Status <span style={styles.required}>*</span>
                </label>
                <select
                  id="plan-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as SubscriptionPlanStatusType)}
                  style={styles.select}
                  disabled={isSubmitting}
                >
                  <option value={SubscriptionPlanStatus.ACTIVE}>Active</option>
                  <option value={SubscriptionPlanStatus.INACTIVE}>Inactive</option>
                  <option value={SubscriptionPlanStatus.ARCHIVED}>Archived</option>
                </select>
                <span style={styles.helpText}>
                  Plan availability status
                </span>
              </div>
            </div>

            {/* Features Section */}
            <div style={styles.featuresSection}>
              <h3 style={styles.featuresSectionTitle}>Features</h3>
              <p style={styles.featuresSectionDescription}>
                Define the features included in this plan for display on pricing pages.
              </p>

              {renderFeatureArrayInput(
                'Feature List',
                featureList,
                setFeatureList,
                20,
                'e.g., Unlimited messages',
                'Features included in this plan'
              )}

              {renderFeatureArrayInput(
                'Highlights',
                highlights,
                setHighlights,
                10,
                'e.g., Priority support',
                'Premium/highlighted features'
              )}

              {renderFeatureArrayInput(
                'Excluded Features',
                excludedFeatures,
                setExcludedFeatures,
                20,
                'e.g., API access',
                'Features NOT included (for comparison)'
              )}
            </div>

            {/* Usage Limits Section */}
            <div style={styles.featuresSection}>
              <h3 style={styles.featuresSectionTitle}>Usage Limits</h3>
              <p style={styles.featuresSectionDescription}>
                Configure usage limits for this plan. Leave number fields empty for unlimited.
              </p>

              {/* Messages Per Day and Max Conversation History Row */}
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label} htmlFor="messages-per-day">
                    Messages Per Day
                  </label>
                  <input
                    id="messages-per-day"
                    type="number"
                    value={messagesPerDay}
                    onChange={(e) => setMessagesPerDay(e.target.value)}
                    placeholder="Unlimited"
                    style={styles.input}
                    min={0}
                    disabled={isSubmitting}
                  />
                  <span style={styles.helpText}>
                    Leave empty for unlimited
                  </span>
                </div>

                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label} htmlFor="max-conversation-history">
                    Max Conversation History
                  </label>
                  <input
                    id="max-conversation-history"
                    type="number"
                    value={maxConversationHistory}
                    onChange={(e) => setMaxConversationHistory(e.target.value)}
                    placeholder="Unlimited"
                    style={styles.input}
                    min={0}
                    disabled={isSubmitting}
                  />
                  <span style={styles.helpText}>
                    Messages to retain (empty = unlimited)
                  </span>
                </div>
              </div>

              {/* Boolean Feature Toggles */}
              <div style={styles.checkboxGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={voiceEnabled}
                    onChange={(e) => setVoiceEnabled(e.target.checked)}
                    style={styles.checkbox}
                    disabled={isSubmitting}
                  />
                  <span style={styles.checkboxText}>Voice/TTS Enabled</span>
                </label>
                <span style={styles.checkboxHelpText}>
                  Allow users to use voice and text-to-speech features
                </span>
              </div>

              <div style={styles.checkboxGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={mediaUploadsEnabled}
                    onChange={(e) => setMediaUploadsEnabled(e.target.checked)}
                    style={styles.checkbox}
                    disabled={isSubmitting}
                  />
                  <span style={styles.checkboxText}>Media Uploads Enabled</span>
                </label>
                <span style={styles.checkboxHelpText}>
                  Allow users to upload files and media
                </span>
              </div>
            </div>

            {/* Platform Integration Section */}
            <div style={styles.featuresSection}>
              <h3 style={styles.featuresSectionTitle}>Platform Integration</h3>
              <p style={styles.featuresSectionDescription}>
                Link this plan to external payment gateways for subscription processing.
              </p>

              {/* Stripe Price ID Field */}
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="stripe-price-id">
                  Stripe Price ID
                </label>
                <input
                  id="stripe-price-id"
                  type="text"
                  value={platformIds.stripePriceId ?? ''}
                  onChange={(e) => setPlatformIds((current) => updatePlatformId(current, 'stripePriceId', e.target.value))}
                  placeholder="price_xxx"
                  style={styles.input}
                  maxLength={100}
                  disabled={isSubmitting}
                />
                <span style={styles.helpText}>
                  Stripe price ID (e.g., price_1234abcd)
                </span>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="stripe-product-id">
                  Stripe Product ID
                </label>
                <input
                  id="stripe-product-id"
                  type="text"
                  value={platformIds.stripeProductId ?? ''}
                  onChange={(e) => setPlatformIds((current) => updatePlatformId(current, 'stripeProductId', e.target.value))}
                  placeholder="prod_xxx"
                  style={styles.input}
                  maxLength={100}
                  disabled={isSubmitting}
                />
                <span style={styles.helpText}>
                  Optional Stripe product ID for existing plan mappings
                </span>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="apple-product-id">
                  Apple Product ID
                </label>
                <input
                  id="apple-product-id"
                  type="text"
                  value={platformIds.appleProductId ?? ''}
                  onChange={(e) => setPlatformIds((current) => updatePlatformId(current, 'appleProductId', e.target.value))}
                  placeholder="com.example.plan"
                  style={styles.input}
                  maxLength={200}
                  disabled={isSubmitting}
                />
                <span style={styles.helpText}>
                  Optional App Store product ID retained for existing plans
                </span>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="google-product-id">
                  Google Product ID
                </label>
                <input
                  id="google-product-id"
                  type="text"
                  value={platformIds.googleProductId ?? ''}
                  onChange={(e) => setPlatformIds((current) => updatePlatformId(current, 'googleProductId', e.target.value))}
                  placeholder="monthly_pro"
                  style={styles.input}
                  maxLength={200}
                  disabled={isSubmitting}
                />
                <span style={styles.helpText}>
                  Optional Play Store product ID retained for existing plans
                </span>
              </div>

              {legacyPlatformIds.length > 0 && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Legacy Platform IDs</label>
                  <div style={styles.featureListContainer}>
                    {legacyPlatformIds.map(([key, rawValue]) => (
                      <div key={key} style={styles.featureInputRow}>
                        <div style={styles.legacyPlatformIdField}>
                          <span style={styles.legacyPlatformIdKey}>{key}</span>
                          <input
                            type="text"
                            value={typeof rawValue === 'string' ? rawValue : JSON.stringify(rawValue)}
                            onChange={(e) => setPlatformIds((current) => ({ ...current, [key]: e.target.value }))}
                            placeholder="Legacy platform ID"
                            style={styles.featureInput}
                            maxLength={500}
                            disabled={isSubmitting}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setPlatformIds((current) => removePlatformId(current, key))}
                          style={styles.removeFeatureButton}
                          disabled={isSubmitting}
                          aria-label={`Remove legacy platform ID ${key}`}
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                  <span style={styles.helpText}>
                    Review and remove legacy IDs here. Clearing a value or removing the row drops it on save.
                  </span>
                </div>
              )}
            </div>

            {/* Display Settings Section */}
            <div style={styles.featuresSection}>
              <h3 style={styles.featuresSectionTitle}>Display Settings</h3>
              <p style={styles.featuresSectionDescription}>
                Configure trial periods and plan prominence for display purposes.
              </p>

              {/* Trial Days Field */}
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="trial-days">
                  Trial Days
                </label>
                <input
                  id="trial-days"
                  type="number"
                  value={trialDays}
                  onChange={(e) => setTrialDays(e.target.value)}
                  placeholder="0"
                  style={styles.input}
                  min={0}
                  max={365}
                  disabled={isSubmitting}
                />
                <span style={styles.helpText}>
                  Number of free trial days (0-365)
                </span>
              </div>

              {/* Is Default Checkbox */}
              <div style={styles.checkboxGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                    style={styles.checkbox}
                    disabled={isSubmitting}
                  />
                  <span style={styles.checkboxText}>Default Plan</span>
                </label>
                <span style={styles.checkboxHelpText}>
                  Mark this as the recommended/default plan for new users
                </span>
              </div>

              {/* Is Featured Checkbox */}
              <div style={styles.checkboxGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={isFeatured}
                    onChange={(e) => setIsFeatured(e.target.checked)}
                    style={styles.checkbox}
                    disabled={isSubmitting}
                  />
                  <span style={styles.checkboxText}>Featured Plan</span>
                </label>
                <span style={styles.checkboxHelpText}>
                  Highlight this plan on pricing pages
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <button
              type="button"
              onClick={onClose}
              style={styles.cancelButton}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                ...((!isValid || isSubmitting) ? styles.submitButtonDisabled : {}),
              }}
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span style={styles.spinner} />
                  {isEditMode ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                <>
                  {isEditMode ? (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M11.667 3.5L5.25 9.917L2.333 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  )}
                  {isEditMode ? 'Save Changes' : 'Create Plan'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '560px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerIcon: {
    fontSize: '24px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
    fontFamily: '"Sentient", Georgia, serif',
  },
  subtitle: {
    fontSize: '13px',
    color: '#9B9285',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: 'none',
    background: 'rgba(93, 86, 78, 0.06)',
    cursor: 'pointer',
    color: '#706A62',
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease',
  },
  form: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  errorMessage: {
    padding: '12px 16px',
    backgroundColor: 'rgba(139, 90, 80, 0.1)',
    color: '#8B5A50',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  formRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '0',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '8px',
  },
  required: {
    color: '#8B5A50',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease',
  },
  select: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
  },
  helpText: {
    display: 'block',
    fontSize: '11px',
    color: '#9B9285',
    marginTop: '6px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
    flexShrink: 0,
  },
  cancelButton: {
    padding: '12px 20px',
    backgroundColor: 'white',
    color: '#5C564E',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },
  submitButton: {
    padding: '12px 20px',
    backgroundColor: '#3D3A36',
    color: '#FAF9F7',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.15s ease',
  },
  submitButtonDisabled: {
    backgroundColor: '#9B9285',
    cursor: 'not-allowed',
  },
  spinner: {
    width: '14px',
    height: '14px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  featuresSection: {
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
  },
  featuresSectionTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: '0 0 4px 0',
    fontFamily: '"Sentient", Georgia, serif',
  },
  featuresSectionDescription: {
    fontSize: '13px',
    color: '#9B9285',
    margin: '0 0 20px 0',
  },
  featureListContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  featureInputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  featureInput: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '8px',
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s ease',
  },
  legacyPlatformIdField: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  legacyPlatformIdKey: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#706A62',
    wordBreak: 'break-word' as const,
  },
  removeFeatureButton: {
    width: '32px',
    height: '32px',
    padding: 0,
    border: 'none',
    borderRadius: '8px',
    backgroundColor: 'rgba(139, 90, 80, 0.1)',
    color: '#8B5A50',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
    flexShrink: 0,
  },
  addFeatureButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '10px 14px',
    border: '1px dashed rgba(93, 86, 78, 0.25)',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: '#706A62',
    fontSize: '13px',
    fontFamily: 'inherit',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  checkboxGroup: {
    marginBottom: '16px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#3D3A36',
  },
  checkboxText: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#3D3A36',
  },
  checkboxHelpText: {
    display: 'block',
    fontSize: '11px',
    color: '#9B9285',
    marginTop: '4px',
    marginLeft: '28px',
  },
};

export default PlanModal;
